import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// Wave 26 — app-wide theme mode (light / dark / system).
//
// Tailwind v4 `dark` variant is already registered in theme.css
// (`@custom-variant dark (&:is(.dark *));`). We toggle `dark` on <html> and
// CSS vars / Tailwind utilities pick it up. `system` follows the live media
// query. Persisted in localStorage under `proofly.theme.v1`; a matching
// inline boot script in `index.html` seeds the class synchronously so the
// first paint doesn't flash light when the user prefers dark.

export type ThemeMode = "light" | "dark" | "system";

interface ThemeContextValue {
  mode: ThemeMode;
  // The resolved theme actually applied — "system" resolves to "light" or
  // "dark" based on the media query. Useful for UI that needs to know
  // what's on-screen (e.g. picking which icon to render on the toggle).
  resolved: "light" | "dark";
  setMode: (next: ThemeMode) => void;
}

const STORAGE_KEY = "proofly.theme.v1";
const VALID_MODES: ReadonlyArray<ThemeMode> = ["light", "dark", "system"];

function isValidMode(value: unknown): value is ThemeMode {
  return (
    typeof value === "string" &&
    (VALID_MODES as ReadonlyArray<string>).includes(value)
  );
}

export function readStoredMode(): ThemeMode {
  if (typeof window === "undefined") return "system";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isValidMode(raw) ? raw : "system";
  } catch {
    return "system";
  }
}

function writeStoredMode(next: ThemeMode): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Ignore quota / privacy-mode errors.
  }
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false
  );
}

function resolveMode(mode: ThemeMode, systemIsDark: boolean): "light" | "dark" {
  if (mode === "system") return systemIsDark ? "dark" : "light";
  return mode;
}

function applyToDocument(resolved: "light" | "dark"): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  root.style.colorScheme = resolved;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface Props {
  children: ReactNode;
}

export function ThemeProvider({ children }: Props) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [systemDark, setSystemDark] = useState<boolean>(() =>
    systemPrefersDark(),
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mql) return;
    const listener = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", listener);
      return () => mql.removeEventListener("change", listener);
    }
    mql.addListener(listener);
    return () => mql.removeListener(listener);
  }, []);

  const resolved = resolveMode(mode, systemDark);
  useEffect(() => {
    applyToDocument(resolved);
  }, [resolved]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      const next = isValidMode(event.newValue) ? event.newValue : "system";
      setModeState(next);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setMode = useCallback((next: ThemeMode) => {
    if (!isValidMode(next)) return;
    setModeState(next);
    writeStoredMode(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolved, setMode }),
    [mode, resolved, setMode],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      mode: "system",
      resolved: systemPrefersDark() ? "dark" : "light",
      setMode: () => {},
    };
  }
  return ctx;
}
